// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { createHandlerChainRunner, HandlerWithResult, HasOnSuccess } from "@insite/client-framework/HandlerCreator";
import { Cart, updateCart } from "@insite/client-framework/Services/CartService";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import loadCurrentCart from "@insite/client-framework/Store/Data/Carts/Handlers/LoadCurrentCart";
import { CartModel } from "@insite/client-framework/Types/ApiModels";
import cloneDeep from "lodash/cloneDeep";

type HandlerType = HandlerWithResult<
    HasOnSuccess<Cart> & { cartToUpdate?: Partial<CartModel> },
    Cart,
    { apiCartToUpdate: CartModel }
>;

export const DispatchBeginUpdatingCart: HandlerType = props => {
    props.dispatch({
        type: "Context/BeginUpdatingCart",
    });
};

export const SetCartStatus: HandlerType = props => {
    const state = props.getState();
    const cart = getCurrentCartState(state).value;

    if (!cart) {
        throw new Error("There was no current cart");
    }

    props.apiCartToUpdate = {
        ...cloneDeep(cart),
        ...props.parameter.cartToUpdate,
    };
};

export const UpdateCart: HandlerType = async props => {
    props.result = (await updateCart({ cart: props.apiCartToUpdate })).cart;
};

export const DispatchCompleteUpdatingCart: HandlerType = props => {
    props.dispatch({
        type: "Context/CompleteUpdatingCart",
    });
};

export const LoadCart: HandlerType = props => {
    props.dispatch(loadCurrentCart());
};

export const ExecuteOnSuccessCallback: HandlerType = props => {
    props.parameter.onSuccess?.(props.result);
};

export const chain = [
    DispatchBeginUpdatingCart,
    SetCartStatus,
    UpdateCart,
    DispatchCompleteUpdatingCart,
    LoadCart,
    ExecuteOnSuccessCallback,
];

const xngUpdateCart = createHandlerChainRunner(chain, "XngUpdateCart");
export default xngUpdateCart;
